import { dataTransformationService } from 'features/charts-v2/services/data-transformation-service'
import { isSentenceGrainBreakdown } from 'features/charts-v2/store'
import { METRICS, type MetricType } from 'features/charts-v2/types'
import {
  ChartProps,
  ChartValidationResult,
} from 'features/charts-v2/types/chart-strategy'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import { ChartConstraintsService } from 'features/charts-v2/types/ChartConstraints'
import { isRidgelineMetric } from 'features/charts-v2/utils/ridgeline-bins'
import {
  countSankeyColumns,
  hasDuplicateSankeyField,
  SANKEY_MAX_COLUMNS,
  SANKEY_MIN_COLUMNS,
} from 'features/charts-v2/utils/sankey-columns'

export class ChartValidationService {
  private static instance: ChartValidationService

  private constructor() {}

  public static getInstance(): ChartValidationService {
    if (!ChartValidationService.instance) {
      ChartValidationService.instance = new ChartValidationService()
    }
    return ChartValidationService.instance
  }

  /**
   * Validate if a chart type can be rendered with the given props
   */
  public validateChart(
    chartType: ChartTypeV2,
    props: ChartProps,
  ): ChartValidationResult {
    if (!props.data || !props.data.data || props.data.data.length === 0) {
      return {
        isValid: false,
        errorTitle: 'No data available',
        errorDescription: 'Try adding some filters.',
      }
    }

    switch (chartType) {
      case ChartTypeV2.BAR_HORIZONTAL_STACKED:
        return this.validateStackedBar(props)

      case ChartTypeV2.AREA_STACKED:
      case ChartTypeV2.AREA_PERCENT:
        return this.validateStackedArea(props)

      case ChartTypeV2.SCATTER:
        return this.validateScatterPlot(props)

      case ChartTypeV2.PIE:
        return this.validatePieChart(props)

      case ChartTypeV2.HISTOGRAM:
        return this.validateHistogram(props)

      case ChartTypeV2.HEATMAP:
        return this.validateHeatmap(props)

      case ChartTypeV2.BUBBLE:
        return this.validateBubbleChart(props)

      case ChartTypeV2.TREEMAP:
        return this.validateTreemap(props)

      case ChartTypeV2.RIDGELINE:
        return this.validateRidgeline(props)

      case ChartTypeV2.SANKEY:
        return this.validateSankey(props)

      case ChartTypeV2.BAR_VERTICAL:
      case ChartTypeV2.LINE:
      case ChartTypeV2.AREA:
      default:
        return this.validateBasicChart(props)
    }
  }

  private validateBasicChart(_props: ChartProps): ChartValidationResult {
    return { isValid: true }
  }

  private validateStackedBar(_props: ChartProps): ChartValidationResult {
    // Stacked bar can now work with any number of breakdowns (0, 1, or 2+)
    // 0 breakdowns: shows a single bar with total
    // 1 breakdown: shows a basic bar chart
    // 2+ breakdowns: shows stacked bars
    return { isValid: true }
  }

  private validateStackedArea(props: ChartProps): ChartValidationResult {
    const hasSummableMetrics = props.selectedMetrics.some((metric) =>
      dataTransformationService.isSummableMetric(metric),
    )

    if (!hasSummableMetrics) {
      return {
        isValid: false,
        errorTitle: 'Unsupported metrics',
        errorDescription:
          'Stacked area charts require summable metrics like record count or sentence count.',
      }
    }
    return { isValid: true }
  }

  private validateScatterPlot(props: ChartProps): ChartValidationResult {
    const constraints = ChartConstraintsService.getConstraints(
      ChartTypeV2.SCATTER,
    )
    const allowedMetrics = props.selectedMetrics.filter(
      (metric) => constraints.metrics.options[metric],
    )

    if (allowedMetrics.length < constraints.metrics.count.min) {
      return {
        isValid: false,
        errorTitle: 'Too few metrics',
        errorDescription: 'Scatter plots require two metrics.',
      }
    }
    return { isValid: true }
  }

  private validatePieChart(props: ChartProps): ChartValidationResult {
    const hasSummableMetrics = props.selectedMetrics.some((metric) =>
      dataTransformationService.isSummableMetric(metric),
    )

    if (!hasSummableMetrics) {
      return {
        isValid: false,
        errorTitle: 'Unsupported metrics',
        errorDescription:
          'Pie charts require summable metrics like record count or sentence count.',
      }
    }
    return { isValid: true }
  }

  private validateHistogram(props: ChartProps): ChartValidationResult {
    const hasSummableMetrics = props.selectedMetrics.some((metric) =>
      dataTransformationService.isSummableMetric(metric),
    )

    if (!hasSummableMetrics) {
      return {
        isValid: false,
        errorTitle: 'Unsupported metrics',
        errorDescription:
          'Histogram charts require summable metrics like record count or sentence count, plus a breakdown.',
      }
    }
    return { isValid: true }
  }

  private validateHeatmap(props: ChartProps): ChartValidationResult {
    const breakdowns = props.selectedBreakdowns || []

    if (breakdowns.length < 2) {
      return {
        isValid: false,
        errorTitle: 'Too few breakdowns',
        errorDescription:
          'Heatmap charts require two breakdowns to create a 2D matrix.',
      }
    }

    return { isValid: true }
  }

  private validateBubbleChart(props: ChartProps): ChartValidationResult {
    const constraints = ChartConstraintsService.getConstraints(
      ChartTypeV2.BUBBLE,
    )
    const allowedMetrics = props.selectedMetrics.filter(
      (metric) => constraints.metrics.options[metric],
    )

    if (allowedMetrics.length < constraints.metrics.count.min) {
      return {
        isValid: false,
        errorTitle: 'Too few metrics',
        errorDescription:
          'Bubble charts require two metrics, or three to show variable bubble sizes.',
      }
    }
    return { isValid: true }
  }

  private validateTreemap(_props: ChartProps): ChartValidationResult {
    return { isValid: true }
  }

  private validateSankey(props: ChartProps): ChartValidationResult {
    const metric = props.selectedMetrics[0]
    if (
      metric !== METRICS.RECORD_COUNT.id &&
      metric !== METRICS.SENTENCE_COUNT.id
    ) {
      return {
        isValid: false,
        errorTitle: 'Unsupported metric',
        errorDescription:
          'Sankey charts size their flows by records or mentions.',
      }
    }

    const breakdowns = props.selectedBreakdowns ?? []
    if (hasDuplicateSankeyField(breakdowns)) {
      return {
        isValid: false,
        errorTitle: 'Repeated breakdown',
        errorDescription:
          'Each breakdown in a Sankey chart must be a different field.',
      }
    }

    const columns = countSankeyColumns(breakdowns)
    if (columns < SANKEY_MIN_COLUMNS || columns > SANKEY_MAX_COLUMNS) {
      return {
        isValid: false,
        errorTitle:
          columns < SANKEY_MIN_COLUMNS ? 'Too few columns' : 'Too many columns',
        errorDescription: `Sankey charts need ${SANKEY_MIN_COLUMNS} to ${SANKEY_MAX_COLUMNS} columns, and a grouped breakdown counts as two (it flows from group to member).`,
      }
    }

    if (
      metric === METRICS.RECORD_COUNT.id &&
      breakdowns.some((breakdown) => isSentenceGrainBreakdown(breakdown))
    ) {
      return {
        isValid: false,
        errorTitle: 'Unsupported metric',
        errorDescription:
          'A sentiment breakdown (or any other per-sentence one) counts mentions, so use the mentions metric.',
      }
    }
    return { isValid: true }
  }

  private validateRidgeline(props: ChartProps): ChartValidationResult {
    if (!isRidgelineMetric(props.selectedMetrics[0])) {
      return {
        isValid: false,
        errorTitle: 'Unsupported metric',
        errorDescription:
          'Ridgeline charts require a sentiment or rating metric.',
      }
    }
    return { isValid: true }
  }

  /**
   * Get required metrics for a chart type
   */
  public getRequiredMetrics(chartType: ChartTypeV2): MetricType[] {
    switch (chartType) {
      case ChartTypeV2.BAR_HORIZONTAL_STACKED:
        return [] // No required metrics - depends on breakdowns instead

      case ChartTypeV2.AREA_STACKED:
      case ChartTypeV2.AREA_PERCENT:
        return [METRICS.RECORD_COUNT.id] // Or SENTENCE_COUNT

      case ChartTypeV2.SCATTER:
        return []

      case ChartTypeV2.PIE:
      case ChartTypeV2.HISTOGRAM:
        return [METRICS.RECORD_COUNT.id] // Or SENTENCE_COUNT

      case ChartTypeV2.HEATMAP:
        return []

      case ChartTypeV2.BUBBLE:
      case ChartTypeV2.TREEMAP:
      case ChartTypeV2.BAR_VERTICAL:
      case ChartTypeV2.LINE:
      case ChartTypeV2.AREA:
      default:
        return []
    }
  }

  /**
   * Get optional metrics for a chart type
   */
  public getOptionalMetrics(chartType: ChartTypeV2): MetricType[] {
    switch (chartType) {
      case ChartTypeV2.TREEMAP:
        return [
          METRICS.RECORD_COUNT.id,
          METRICS.SENTENCE_COUNT.id,
          METRICS.SENTIMENT.id,
          METRICS.RATING.id,
          METRICS.NET_PROMOTER_SCORE.id,
        ]

      case ChartTypeV2.HEATMAP:
        return [
          METRICS.SENTIMENT.id,
          METRICS.RATING.id,
          METRICS.RECORD_COUNT.id,
          METRICS.SENTENCE_COUNT.id,
        ]

      default:
        return []
    }
  }
}

export const chartValidationService = ChartValidationService.getInstance()
